import { useCallback, useRef } from "react";

import { GEO_OPTIONS, GpsReading } from "utils/geolocation";

// High-precision location acquisition for a mobile browser.
//
// Browsers' Geolocation API provides getCurrentPosition (single reading) and
// watchPosition (continuous stream). We use watchPosition because mobile GPS
// hardware typically starts with a low-accuracy cellular/Wi-Fi fix and progressively
// refines it as it locks onto more satellites. By listening to the stream for a
// few seconds, we can discard early inaccurate readings and keep the most precise one.
// We resolve the Promise early if we reach an accuracy that is "good enough",
// minimizing wait time for the user.

const GPS_MAXIMUM_WAIT_TIME_MS = 10000; // Total time to wait for the GPS to refine its accuracy
const DESIRED_GPS_ACCURACY_METERS = 25; // If accuracy reaches this threshold (or lower), stop listening early

export type GpsAcquisitionStatus = "ok" | "permission_denied" | "unavailable" | "timeout";

export interface GpsAcquisitionResult {
	status: GpsAcquisitionStatus;
	reading?: GpsReading;
	error?: string;
}

export const useHighPrecisionGpsLocation = () => {
	// We use a ref to store the pending Promise. This acts as a lock to prevent
	// overlapping GPS acquisitions if the user double-taps the capture button.
	const pendingGpsRequestRef = useRef<Promise<GpsAcquisitionResult> | null>(null);

	const acquireBestGpsLocation = useCallback((): Promise<GpsAcquisitionResult> => {
		if (pendingGpsRequestRef.current) return pendingGpsRequestRef.current;

		const acquisitionPromise = new Promise<GpsAcquisitionResult>(resolve => {
			if (typeof navigator === "undefined" || !navigator.geolocation) {
				resolve({ status: "unavailable", error: "Geolocation API is not supported in this browser." });
				return;
			}

			let mostAccurateReading: GpsReading | null = null;
			let geolocationWatchId: number | null = null;
			let timeoutTimer: ReturnType<typeof setTimeout> | null = null;
			let isAcquisitionFinalized = false;

			const cleanupResources = () => {
				if (geolocationWatchId !== null) navigator.geolocation.clearWatch(geolocationWatchId);
				if (timeoutTimer !== null) clearTimeout(timeoutTimer);
			};

			const finalizeGpsAcquisition = (result: GpsAcquisitionResult) => {
				if (isAcquisitionFinalized) return;
				isAcquisitionFinalized = true;
				cleanupResources();
				console.log("[GPS Testing] Location settled with status:", result.status, result.reading || result.error);
				resolve(result);
			};

			// Fallback timer: if the GPS doesn't reach the desired accuracy within the maximum wait time,
			// we finalize the acquisition using the best reading we have collected so far.
			timeoutTimer = setTimeout(() => {
				if (mostAccurateReading) finalizeGpsAcquisition({ status: "ok", reading: mostAccurateReading });
				else
					finalizeGpsAcquisition({ status: "timeout", error: "Failed to acquire a GPS lock within the allowed time." });
			}, GPS_MAXIMUM_WAIT_TIME_MS);

			// Start listening to the device's geolocation stream
			geolocationWatchId = navigator.geolocation.watchPosition(
				position => {
					const currentReading: GpsReading = {
						latitude: position.coords.latitude,
						longitude: position.coords.longitude,
						// Accuracy is the radius in meters defining a 95% confidence level
						accuracy: position.coords.accuracy
					};
					console.log("[GPS Testing] New reading acquired, accuracy:", currentReading.accuracy);

					if (!mostAccurateReading || currentReading.accuracy < mostAccurateReading.accuracy) {
						mostAccurateReading = currentReading;
					}

					// If the reading is precise enough, stop listening to save battery and reduce latency
					if (mostAccurateReading.accuracy <= DESIRED_GPS_ACCURACY_METERS) {
						finalizeGpsAcquisition({ status: "ok", reading: mostAccurateReading });
					}
				},
				error => {
					// Handle native GeolocationPositionError cases
					if (error.code === error.PERMISSION_DENIED) {
						finalizeGpsAcquisition({ status: "permission_denied", error: error.message });
						return;
					}
					// If the signal is unavailable or times out natively, fallback to whatever reading we managed to get
					if (mostAccurateReading) finalizeGpsAcquisition({ status: "ok", reading: mostAccurateReading });
					else finalizeGpsAcquisition({ status: "unavailable", error: error.message });
				},
				GEO_OPTIONS
			);
		}).finally(() => {
			pendingGpsRequestRef.current = null;
		});

		pendingGpsRequestRef.current = acquisitionPromise;
		return acquisitionPromise;
	}, []);

	return { acquireBestGpsLocation };
};
